import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { Today, TODAY } from "../customers/Today.js";
import { Companies } from "../customers/Companies.js";
import { SetUp } from "../customers/SetUp.js";
import { useNarrow } from "../ui/narrow.js";
import { SideHead, SideMenu, sideIcon } from "./chrome.js";

/**
 * **Customers** (23, [ADR 0037](../../../../docs/adr/0037-customers-is-one-loop-configured-from-the-business-page-for-businesses-and-for-the-public.md)):
 * finding this business its next customer.
 *
 * **Three screens, and it was six.** The six were three views of one table — one showed what was ready to write
 * to, one showed what had been sent, and one showed everything with filter chips named after the other two — and
 * the tab opened on Find, which is the most expensive and most optional thing on it. Every empty state pointed at
 * another tab, which is what a tab with no front door looks like.
 *
 * The loop is the product; these screens are how somebody supervises it:
 *
 * - **Today** — who is waiting for you, in that order: an answer, a letter to approve, a decision, then the loop
 *   reporting on itself. The front door, and a badge when somebody has actually replied.
 * - **Companies** — the whole list. A row opens the company itself, which is a panel and not a fourth screen.
 * - **Set up** — one checklist, in the order a new workspace needs it.
 *
 * Nothing that exists for a team is drawn: no *whose*, no assigning a market to a member. The columns are still
 * in the tables, and a workspace with a second person is when they start being worth showing.
 */

type Screen = "today" | "companies" | "setup";
const SCREENS: ReadonlyArray<{ key: Screen; label: string; hint: string; icon: string }> = [
  { key: "today", label: "Today", hint: "who is waiting for you", icon: "chat" },
  { key: "companies", label: "Companies", hint: "the whole list", icon: "list" },
  { key: "setup", label: "Set up", hint: "five things, in order", icon: "pencil" },
];

export function Customers({ onMailbox }: { onMailbox: () => void }) {
  const [screen, setScreen] = useState<Screen>("today");
  const narrow = useNarrow();
  // The one number worth putting on the menu: somebody answered, and that is a person's job.
  const today = useQuery({ queryKey: TODAY, queryFn: () => api.today() });
  const waiting = today.data?.replies.length ?? 0;

  return (
    <div className={narrow ? "h-full flex flex-col min-h-0" : "h-full flex min-h-0"}>
      <SideMenu label="Customers" current={screen} onPick={setScreen} narrow={narrow}
        head={<SideHead icon="users" title="Customers" note="finding your next customer" />}
        items={SCREENS.map((s) => ({ ...s, mark: sideIcon(s.icon), ...(s.key === "today" ? { count: waiting } : {}) }))} />
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        <div className="px-4 md:px-6 py-5 flex flex-col gap-4">
          {screen === "today" ? <Today />
            : screen === "companies" ? <Companies />
              : <SetUp onMailbox={onMailbox} />}
        </div>
      </main>
    </div>
  );
}
